import React, { useState } from 'react';
import { VideoMedia } from '../../types';

interface VideoDisplayProps {
  video: VideoMedia;
  className?: string;
}

const VideoDisplay: React.FC<VideoDisplayProps> = ({ video, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Function to extract YouTube video ID from URL
  const getYouTubeId = (url: string) => {
    const regex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // Function to extract Vimeo video ID from URL
  const getVimeoId = (url: string) => {
    const regex = /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/[^/]\/videos\/|album\/\d+\/video\/|)(\d+)(?:$|\/|\?))/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const renderPlayer = () => {
    // Default is to assume it's a hosted video
    if (!video.provider || video.provider === 'hosted') {
      return (
        <video
          src={video.url}
          poster={video.thumbnailUrl}
          controls
          className="w-full rounded-lg"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedData={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    // YouTube embed
    if (video.provider === 'youtube') {
      const videoId = getYouTubeId(video.url);
      if (!videoId) {
        setHasError(true);
        return (
          <div className="text-red-500 p-4">
            Invalid YouTube URL
          </div>
        );
      }

      return (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          className="w-full rounded-lg aspect-video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        ></iframe>
      );
    }

    // Vimeo embed
    if (video.provider === 'vimeo') {
      const videoId = getVimeoId(video.url);
      if (!videoId) {
        setHasError(true);
        return (
          <div className="text-red-500 p-4">
            Invalid Vimeo URL
          </div>
        );
      }

      return (
        <iframe
          src={`https://player.vimeo.com/video/${videoId}`}
          className="w-full rounded-lg aspect-video"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        ></iframe>
      );
    }

    return (
      <div className="text-red-500 p-4">
        Unsupported video provider
      </div>
    );
  };

  if (hasError) {
    return (
      <div className={`rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center ${className}`}>
        <div className="text-gray-500 p-4 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p>Failed to load video</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden ${className}`}>
      {!isLoaded && !isPlaying && (
        <div className="relative w-full aspect-video bg-gray-200 animate-pulse flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      )}
      
      <div className={`transition-opacity duration-300 ${isLoaded || isPlaying ? 'opacity-100' : 'opacity-0'}`}>
        {renderPlayer()}
      </div>
      
      {video.caption && (
        <div className="mt-1 text-sm text-gray-500 italic">
          {video.caption}
        </div>
      )}
    </div>
  );
};

export default VideoDisplay; 